import localForage from 'localforage';

interface AITranslationPreference {
  value: boolean;
  expiresAt: number;
}

interface UserPreferences {
  [userId: string]: AITranslationPreference;
}

const STORAGE_KEY = 'aiTranslationDontAskAgain';
const ONE_MONTH_IN_MS = 30 * 24 * 60 * 60 * 1000; // 30 days in milliseconds

/**
 * Get current user ID from session storage
 */
function getCurrentUserId(): string | null {
  return sessionStorage.getItem('pm-userId');
}

/**
 * Get stored AI translation preferences from localForage
 */
async function getStoredPreferences(): Promise<UserPreferences> {
  try {
    const stored = await localForage.getItem<UserPreferences>(STORAGE_KEY);

    return stored || {};
  } catch {
    return {};
  }
}

/**
 * Save preferences to localForage
 */
async function savePreferences(preferences: UserPreferences): Promise<void> {
  try {
    await localForage.setItem(STORAGE_KEY, preferences);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Failed to save AI translation preferences:', error);
  }
}

/**
 * Check if user has selected "don't ask again" for AI translation confirmation
 */
export async function hasAITranslationDontAskAgain(): Promise<boolean> {
  const userId = getCurrentUserId();
  if (!userId) {
    return false;
  }

  const preferences = await getStoredPreferences();
  const userPref = preferences[userId];

  if (!userPref) {
    return false;
  }

  // Check if preference has expired
  // if (userPref.expiresAt <= Date.now()) {
  //   // Clean up expired preference
  //   delete preferences[userId];
  //   await savePreferences(preferences);

  //   return false;
  // }

  return userPref.value;
}

/**
 * Set "don't ask again" preference for AI translation confirmation
 */
export async function setAITranslationDontAskAgain(value: boolean): Promise<void> {
  const userId = getCurrentUserId();
  if (!userId) {
    // eslint-disable-next-line no-console
    console.warn('Cannot save AI translation preference: no user ID found');

    return;
  }

  const preferences = await getStoredPreferences();

  if (value) {
    preferences[userId] = {
      value: true,
      expiresAt: Date.now() + ONE_MONTH_IN_MS,
    };
  } else {
    // If setting to false, just remove the preference
    delete preferences[userId];
  }

  await savePreferences(preferences);
}
